import { DateTime } from "luxon";

import { DateHelper } from "@/Common/Helpers/DateHelper";
import { PortalHelper } from "@/Common/Helpers/PortalHelper";
import { MyleObjectType } from "@/Common/Models/Enum/MyleObjectType";
import { PortalLinkedItemType } from "@/Common/Models/Portal/Enums/PortalLinkedItemType";
import { PortalSyncStatus } from "@/Common/Models/Portal/Enums/PortalSyncStatus";
import { SharedItem } from "@/Common/Models/Portal/SharedItem/SharedItem";
import { SharedItemMessage } from "@/Common/Models/Portal/SharedItem/SharedItemMessage";
import { IPortalShareableItem } from "@/Common/Models/Portal/UI/IPortalShareableItem";
import { WorkSafeBcFormType } from "../Constants/WorkSafeBcFormType";

class WorkSafeBcFormLight implements IPortalShareableItem {
    public Id: string;
    public FormType: WorkSafeBcFormType;
    public CreatedDate: Date;
    public InjuryOrFirstTreatmentForConditionDate: Date;
    public EncounterId: string;
    public SharedItemId: string;
    public SharedItemDate: Date;
    public ModifiedOn: Date;

    // for PORTAL
    public SharedItem: SharedItem;
    public SharedItemMessages: SharedItemMessage[];

    // UI only
    public isExtended: boolean;

    public constructor(init?: Partial<WorkSafeBcFormLight>) {
        Object.assign(this, init);
    }

    public toSelectingString(): string {
        return cacheStore.GetString("WorkSafeBc_GenerateFromLabel", { formType: this.formTypeLabel() })
            + (this.CreatedDate ? ` - ${cacheStore.GetString("CreationDateLabel")}: ${DateHelper.formatIsoDateOnly(this.CreatedDate) || ""}` : "")
            + (this.InjuryOrFirstTreatmentForConditionDate ? ` - ${cacheStore.GetString("DateOfInjuryLabel")}: ${DateHelper.formatIsoDateOnly(this.InjuryOrFirstTreatmentForConditionDate) || ""}` : "");
    }

    public formTypeLabel(): string {
        switch (this.FormType) {
            case WorkSafeBcFormType.Form8:
                return cacheStore.GetString("WorkSafeBc_Form8ShortLabel");
            case WorkSafeBcFormType.Form11:
                return cacheStore.GetString("WorkSafeBc_Form11ShortLabel");
            default:
                return "";
        }
    }

    public toHeaderString(): string {
        switch (this.FormType) {
            case WorkSafeBcFormType.Form8:
                return cacheStore.GetString("WorkSafeBc_PhysicianFirstReportF8Label");
            case WorkSafeBcFormType.Form11:
                return cacheStore.GetString("WorkSafeBc_WorkerConditionOrTreatmentHasChangedF11");
            default:
                return "";
        }
    }

    // PORTAL FIELDS
    public getSharedItemMessages(): SharedItemMessage[] {
        this.SharedItemMessages && this.SharedItemMessages.forEach(p => {
            p.tmpIdForUI = SharedItemMessage.getTempIdForUi(p);
        });

        return this.SharedItemMessages;
    }

    public getSharedItem(): SharedItem {
        return this.SharedItem;
    }

    public getSharedItemId(): string {
        return this.SharedItem?.SharedItemId;
    }

    public getGlobalPortalStatus(): PortalSyncStatus {
        return PortalHelper.getGlobalPortalStatus(this.getSharedItem(), this.getSharedItemMessages());
    }

    public getItemTypeHeader(): string {
        return "";
    }

    public getItemTitle(): string {
        return this.formTypeLabel();
    }

    public getDefaultSubject(): string {
        return this.toHeaderString();
    }

    public getPortalObjectId(): string {
        return this.Id;
    }

    public getPortalLinkedItemType(): PortalLinkedItemType {
        return PortalLinkedItemType.WorkSafeBcForm;
    }

    public getSharedItemPortalStatus(): PortalSyncStatus {
        return PortalHelper.getLinkedItemPortalStatus(this.getSharedItem());
    }

    public hasBeenModifiedSinceLastShared(): boolean {
        return (this.SharedItemDate && this.ModifiedOn)
            && (DateTime.fromISO(this.SharedItemDate.toString()) < DateTime.fromISO(this.ModifiedOn.toString()));
    }

    public hasBeenSharedOnPreviousEncounter(): boolean {
        return false;
    }

    public getMyleObjectType(): MyleObjectType {
        return MyleObjectType.WorkSafeBc;
    }

    public updateShareableModel(sharedItem: SharedItem, sharedItemMessages: SharedItemMessage[]): void {
        this.SharedItem = sharedItem;
        this.SharedItemId = sharedItem.SharedItemId;
        this.SharedItemDate = sharedItem.DateModified ?? sharedItem.DateCreated;
        this.SharedItemMessages = sharedItemMessages;
        this.setSharedItemMessagesSelectStatus();
    }

    public setSharedItemMessagesSelectStatus(): void {
        for (let message of this.SharedItemMessages || [])
            message.IsSelected = message.Status === PortalSyncStatus.Pending || message.Status === PortalSyncStatus.Viewed;
    }
}

export { WorkSafeBcFormLight };
